import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { generateProfilePictureSVG, generateGradientFromUsername } from '../utils/profilePicture'
import './UserDropdown.css'

interface UserDropdownProps {
  onNavigate?: () => void
}

const UserDropdown = ({ onNavigate }: UserDropdownProps) => {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  if (!user) return null

  const handleLogout = async () => {
    setIsOpen(false)
    await logout()
    navigate('/', { replace: true })
  }

  const handleSettings = () => {
    setIsOpen(false)
    if (onNavigate) onNavigate()
    navigate('/settings')
  }

  const handleAdmin = () => {
    setIsOpen(false)
    if (onNavigate) onNavigate()
    navigate('/admin')
  }

  // Use 40px for both mobile and desktop to match notification icon
  const profilePictureSize = 40
  const profilePicture = user.profilePicture || generateProfilePictureSVG(user.username, profilePictureSize)

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsOpen(!isOpen)
  }

  return (
    <div className="user-dropdown" ref={dropdownRef}>
      <button
        className="user-dropdown-trigger"
        onClick={handleToggle}
        aria-label="User menu"
        aria-expanded={isOpen}
      >
        <div
          className="user-avatar-small"
          style={{ background: generateGradientFromUsername(user.username) }}
          dangerouslySetInnerHTML={{ __html: profilePicture }}
        />
        <span className="user-username">{user.username}</span>
      </button>

      {isOpen && (
        <div className="user-dropdown-menu">
          <div className="user-dropdown-header">
            <div
              className="user-avatar-large"
              style={{ background: generateGradientFromUsername(user.username) }}
              dangerouslySetInnerHTML={{ __html: generateProfilePictureSVG(user.username, 60) }}
            />
            <div className="user-dropdown-info">
              <div className="user-dropdown-username">{user.username}</div>
              <div className="user-dropdown-email">{user.email}</div>
            </div>
          </div>
          <div className="user-dropdown-divider"></div>
          <button className="user-dropdown-item" onClick={handleSettings}>
            <i className="fas fa-cog"></i>
            <span>settings</span>
          </button>
          {user.isAdmin && (
            <button className="user-dropdown-item" onClick={handleAdmin}>
              <i className="fas fa-shield-alt"></i>
              <span>admin panel</span>
            </button>
          )}
          <div className="user-dropdown-divider"></div>
          <button className="user-dropdown-item user-dropdown-item-danger" onClick={handleLogout}>
            <i className="fas fa-sign-out-alt"></i>
            <span>logout</span>
          </button>
        </div>
      )}
    </div>
  )
}

export default UserDropdown

